import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  InvoicesDetailPageAvailabilitiesDocument,
  InvoicesDetailPageInvoiceDocument,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

export const useInvoiceDetail = ({
  invoiceId,
  companyId,
}: {
  invoiceId: number
  companyId: number
}) => {
  const { data, loading: loadingAvailabilities } = useQuery(
    InvoicesDetailPageAvailabilitiesDocument,
    {
      variables: {
        companyId: companyId,
        moduleIds: [
          AvailableModules.it_management,
          AvailableModules.procurement,
          AvailableModules.expenses,
          AvailableModules.projects,
        ],
        featureIds: [Features.accounts_payable_payments, Features.finance_ledger_accounts_core, Features.projects_costs],
      },
    }
  )

  const { data: associationData, loading: associationLoading } = useQuery(
    InvoicesDetailPageInvoiceDocument,
    {
      variables: {
        companyId: companyId,
        id: invoiceId,
      },
    }
  )
  // This query was split in two (InvoicesDetailPageAvailabilities and InvoicesDetailPageInvoice) because we had reach the max query complexity

  const financialDocumentsPaymentsAvailable =
    data?.companies?.featureAvailabilities.find(
      (featureAvailability) => featureAvailability.id === Features.accounts_payable_payments
    )?.available ?? false
  const isJournalEntriesManagementFeatureAvailable =
    data?.companies?.featureAvailabilities.find(
      (featureAvailability) => featureAvailability.id === Features.finance_ledger_accounts_core
    )?.available ?? false
  const reconciliationsSeeFinancialDocumentsPage =
    data?.banking?._policies?.reconciliationsSeeFinancialDocumentsPage?.can ?? false
  const reconciliationsSeeJournalEntriesPage =
    data?.banking?._policies?.reconciliationsSeeJournalEntriesPage?.can ?? false

  const isSoftwareModuleEnabled =
    data?.companies?.moduleAvailabilities.find(
      (moduleAvailability) => moduleAvailability.id === AvailableModules.it_management
    )?.available ?? false
  const isProcurementModuleEnabled =
    data?.companies?.moduleAvailabilities.find(
      (moduleAvailability) => moduleAvailability.id === AvailableModules.procurement
    )?.available ?? false
  const isExpensesModuleEnabled =
    data?.companies?.moduleAvailabilities.find(
      (moduleAvailability) => moduleAvailability.id === AvailableModules.expenses
    )?.available ?? false
  const isProjectsModuleEnabled =
    data?.companies?.moduleAvailabilities.find(
      (moduleAvailability) => moduleAvailability.id === AvailableModules.projects
    )?.available ?? false
  const isProjectsCostsFeatureEnabled =
    data?.companies?.featureAvailabilities.find(
      (featureAvailability) => featureAvailability.id === Features.projects_costs
    )?.available ?? false

  const legalEntities = data?.companies?.legalEntitiesConnection.nodes || []
  const taxTypes = associationData?.finance?.taxTypes || []
  const canManageJournalEntries =
    data?.finance?._policies?.journalEntriesManagePurchase?.can ?? false
  const journalLines = associationData?.finance.journalLinesConnection.nodes ?? []
  const journalEntries = associationData?.finance.journalEntriesConnection.nodes ?? []
  const invoice = useUnsafeMemo(
    () => associationData?.finance?.financialDocuments[0],
    [associationData]
  )

  const file = invoice?.file

  const loading = loadingAvailabilities || associationLoading

  return {
    loading,
    invoice,
    financialDocumentsPaymentsAvailable,
    isJournalEntriesManagementFeatureAvailable,
    isSoftwareModuleEnabled,
    isProcurementModuleEnabled,
    isExpensesModuleEnabled,
    isProjectsModuleEnabled,
    isProjectsCostsFeatureEnabled,
    legalEntities,
    taxTypes,
    file,
    canManageJournalEntries,
    reconciliationsSeeJournalEntriesPage,
    reconciliationsSeeFinancialDocumentsPage,
    journalLines,
    journalEntries,
  }
}
